import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core';
import { ButtonComponent, ButtonSizeType, TableColumn, ToastNotificationComponent } from '@ui/aos-lib';
import { ListingPageService } from 'src/app/shared/components/listing-page/listing-page.service';
import { RequestProcessingService } from '../../services/request-processing.service';
import { RequestProcessingTableRow, RetryRequestResponse } from '../../services/request-processing.types';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { TranslateService } from 'src/app/services/translate/translate.service';
import { AsyncPipe } from '@angular/common';
import { Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ToastNotificationComponent],
  imports: [ButtonComponent, TranslateModule, AsyncPipe],
  template: `
    @if ((canRetry$ | async) && item.canRetry === true && item.requestId) {
      <ui-button
        (click)="retry()"
        [size]="buttonSize"
        [themeColor]="'primary'"
        >{{ retryLabel | translate }}
      </ui-button>
    }
  `
})
export class RequestProcessingActionsColumnRetryComponent implements OnInit {
  protected readonly requestProcessingService = inject(RequestProcessingService);
  private readonly listingPageService = inject(ListingPageService);
  private readonly toastNotificationComponent = inject(ToastNotificationComponent);
  private readonly translate = inject(TranslateService);

  protected successNotificationTitle =
    TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.NOTIFICATIONS.TITLE.REQUEST_RETRY_SUCCESS;
  protected successNotificationDetail =
    TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.NOTIFICATIONS.DETAIL.REQUEST_RETRY_SUCCESS;
  protected failedNotificationTitle =
    TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.NOTIFICATIONS.TITLE.REQUEST_RETRY_FAILED;
  protected failedNotificationDetail =
    TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.NOTIFICATIONS.DETAIL.REQUEST_RETRY_FAILED;
  protected readonly retryLabel = TranslateRegistry.get.TWO_WAY_REQUEST_PROCESSING.BUTTON.RETRY;

  @Input() item!: RequestProcessingTableRow;
  @Input() column!: TableColumn;

  protected readonly buttonSize: ButtonSizeType = 'small';
  protected canRetry$: Observable<boolean> = of(false);

  ngOnInit(): void {
    this.canRetry$ = this.requestProcessingService.canRetry$(this.item.commandType);
  }

  protected retry(): void {
    const requestId = this.item.requestId;
    if (!requestId) {
      return;
    }

    this.requestProcessingService
      .retryRequest(requestId)
      .pipe(take(1))
      .subscribe({
        next: response => {
          if (response.success === true) {
            const toastTitle = this.translate.instant(this.successNotificationTitle);
            const toastDetail = this.translate.instant(this.successNotificationDetail);

            this.toastNotificationComponent.showSuccessToast(toastTitle, toastDetail);
            this.listingPageService.refresh();
            return;
          }

          const toastTitle = this.translate.instant(this.failedNotificationTitle);
          const toastDetail = this.getRetryFailureMessage(response);

          this.toastNotificationComponent.showErrorToast(toastTitle, toastDetail);
        }
      });
  }

  private getRetryFailureMessage(response: RetryRequestResponse): string {
    const backendMessage = response.errorMessage?.trim();

    if (backendMessage) {
      return backendMessage;
    }

    return this.translate.instant(this.failedNotificationDetail);
  }
}
